import { logger } from 'src/commons/utils/logger';
import { InternalChromeConfig } from '@amzn/aac-chrome-internal-api';

export const ENABLE_LEGACY_REDIRECT_KEY = 'enableLegacyConversionMetrics';
export const DISPLAY_LEGACY_BANNER_KEY = 'isDspOverviewRolloverEnabled';
export const DVA_AD_GROUP_FLAT_VIEW_KEY = 'isDvaAdGroupFlatViewEnabled';

/**
 * Session storage keys that get appended as query params to updated URLs.
 * Each key is only appended if its value is 'true' in session storage.
 */
export const LEGACY_METRICS_BANNER_KEYS: readonly string[] = [
  ENABLE_LEGACY_REDIRECT_KEY,
  DISPLAY_LEGACY_BANNER_KEY,
  DVA_AD_GROUP_FLAT_VIEW_KEY,
];

/**
 * For each legacy metrics banner key, if the current page URL has it set to 'true'
 * but session storage does not, persist it. This handles landing on a new
 * domain after a region switch — the params were appended to the redirect
 * URL on the previous domain and need to be carried into this session.
 *
 * Only runs once per page load to avoid re-writing values that were
 * intentionally removed from session storage while the URL still contains them.
 */
let hasAttemptedSync = false;
export function syncUpdateParamsFromUrlToStorage(): void {
  if (hasAttemptedSync) return;
  hasAttemptedSync = true;
  try {
    const url = new URL(window.location.href);
    for (const key of LEGACY_METRICS_BANNER_KEYS) {
      if (
        url.searchParams.get(key) === 'true' &&
        window.sessionStorage.getItem(key) !== 'true'
      ) {
        window.sessionStorage.setItem(key, 'true');
        logger.info(`linkUpdater: synced ${key} from URL to session storage`);
      }
    }
  } catch (e: any) {
    logger.warn(
      'linkUpdater: failed to sync legacy metrics params from URL to storage',
      e,
    );
  }
}

const ENTITY_ID_PREFIX = 'ENTITY';

/**
 * Resolves the entityId for campaign-manager paths using a fallback chain:
 * 1. The clicked link's ?entityId param (if it starts with "ENTITY")
 * 2. The entityId parsed from the current page URL's path (e.g. /dsp/ENTITY123/...)
 * 3. The current page URL's ?entityId param (if it starts with "ENTITY")
 * 4. The entityId from internalChromeConfig (if it starts with "ENTITY")
 */
function resolveEntityId(
  linkUrl: URL,
  currentPageHref: string,
  internalChromeConfig: InternalChromeConfig,
): string | null {
  const linkEntityId = linkUrl.searchParams.get('entityId');
  if (linkEntityId?.startsWith(ENTITY_ID_PREFIX)) return linkEntityId;

  let currentUrl: URL;
  try {
    currentUrl = new URL(currentPageHref);
  } catch (e: any) {
    logger.warn('linkUpdater: failed to parse current page URL', e);
    return null;
  }

  const pathEntityId = extractEntityIdFromPath(currentUrl.pathname);
  if (pathEntityId) return pathEntityId;

  const currentParamEntityId = currentUrl.searchParams.get('entityId');
  if (currentParamEntityId?.startsWith(ENTITY_ID_PREFIX))
    return currentParamEntityId;

  if (internalChromeConfig.entityId?.startsWith(ENTITY_ID_PREFIX))
    return internalChromeConfig.entityId;

  return null;
}

function extractEntityIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/dsp\/([^/]+)/);
  if (match && match[1].startsWith(ENTITY_ID_PREFIX)) return match[1];
  return null;
}

/**
 * Appends each legacy metrics banner key to the URL if its value is 'true' in session
 * storage and not already present on the URL. Silently degrades if
 * sessionStorage is unavailable.
 */
export function appendActiveParams(url: URL): void {
  try {
    for (const key of LEGACY_METRICS_BANNER_KEYS) {
      if (
        url.searchParams.get(key) !== 'true' &&
        window.sessionStorage.getItem(key) === 'true'
      ) {
        url.searchParams.set(key, 'true');
      }
    }
  } catch (e: any) {
    logger.warn(
      'linkUpdater: failed to read sessionStorage in appendActiveParams',
      e,
    );
  }
}

interface UpdateContext {
  url: URL;
  currentPageHref: string;
  internalChromeConfig: InternalChromeConfig;
}

interface UpdateRule {
  pattern: RegExp;
  update: (ctx: UpdateContext) => URL | null;
}

const UPDATE_RULES: readonly UpdateRule[] = [
  // Rule 1: /campaign-manager/all-campaigns -> entity/orders
  {
    pattern: /^\/campaign-manager\/all-campaigns(\/|$)/,
    update: ({ url, currentPageHref, internalChromeConfig }) => {
      // Do not update account-switching links from the global account selector
      if (url.searchParams.has('switchFromEntity')) return null;

      const entityId = resolveEntityId(
        url,
        currentPageHref,
        internalChromeConfig,
      );
      if (!entityId) return null;

      const updated = new URL(
        `/dsp/${encodeURIComponent(entityId)}/orders`,
        url.origin,
      );
      appendActiveParams(updated);
      return updated;
    },
  },
  // Rule 2: /campaign-manager/ad-groups -> entity/line-items
  {
    pattern: /^\/campaign-manager\/ad-groups(\/|$)/,
    update: ({ url, currentPageHref, internalChromeConfig }) => {
      const entityId = resolveEntityId(
        url,
        currentPageHref,
        internalChromeConfig,
      );
      if (!entityId) return null;

      const updated = new URL(
        `/dsp/${encodeURIComponent(entityId)}/line-items`,
        url.origin,
      );
      appendActiveParams(updated);
      return updated;
    },
  },
  // Rule 3: /dsp/{entityId}/overview -> entity/orders
  {
    pattern: /^\/dsp\/[^/]+\/overview(\/|$)/,
    update: ({ url }) => {
      const entityId = extractEntityIdFromPath(url.pathname);
      if (!entityId) return null;

      const updated = new URL(
        `/dsp/${encodeURIComponent(entityId)}/orders`,
        url.origin,
      );
      appendActiveParams(updated);
      return updated;
    },
  },
  // Rule 4: /dsp/{entityId}/orders or /dsp/{entityId}/line-items — append params
  {
    pattern: /^\/dsp\/[^/]+\/(orders|line-items)\/?$/,
    update: ({ url }) => {
      const updated = new URL(url.href);
      appendActiveParams(updated);
      // If nothing was appended, no update is needed
      if (updated.href === url.href) return null;
      return updated;
    },
  },
  // Rule 5: /dsp|aap/{entityId}/advertisers/{advertiserId}/orders or line-items — append params
  {
    pattern: /^\/(dsp|aap)\/[^/]+\/advertisers\/[^/]+\/(orders|line-items)\/?$/,
    update: ({ url }) => {
      const updated = new URL(url.href);
      appendActiveParams(updated);
      // If nothing was appended, no update is needed
      if (updated.href === url.href) return null;
      return updated;
    },
  },
];

/**
 * Applies the first matching update rule to the given URL.
 * Returns the updated URL string, or the original href unchanged if no
 * update is applicable.
 */
export function getUpdatedUrl(
  href: string,
  currentPageHref: string,
  internalChromeConfig: InternalChromeConfig,
): string {
  // Determine if the href is a path-absolute relative reference (e.g. "/dsp/...")
  const isRelative = href.startsWith('/') && !href.startsWith('//');

  let url: URL;
  try {
    url = isRelative ? new URL(href, window.location.origin) : new URL(href);
  } catch (e: any) {
    logger.warn('linkUpdater: failed to parse href', e);
    return href;
  }

  for (const rule of UPDATE_RULES) {
    if (rule.pattern.test(url.pathname)) {
      const updated = rule.update({
        url,
        currentPageHref,
        internalChromeConfig,
      });
      if (!updated) return href;
      // Preserve the original format: return relative path if input was relative
      if (isRelative) {
        return updated.pathname + updated.search + updated.hash;
      }
      return updated.href;
    }
  }

  return href;
}
